import { useForm } from '@mantine/form';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { RegisterUserDto } from 'src/Services/API/generated';

export interface RegisterFormValues {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  acceptedTermsPrivacy: boolean;
  newsletterOptIn: boolean;
}

export const useRegisterForm = (initialEmail?: string) => {
  const { t } = useTranslation();

  return useForm<RegisterFormValues, (value: RegisterFormValues) => RegisterUserDto>({
    initialValues: {
      email: initialEmail ?? '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      acceptedTermsPrivacy: true,
      newsletterOptIn: false,
    },
    validate: {
      email: (value: string) =>
        /^\S+@\S+$/.test(value) ? null : t('forms.invalidEmail', 'Mail invalide'),
      password: (value: string) => {
        if (value.length < 8)
          return t('forms.passwordTooShort', 'Le mot de passe doit contenir au moins 8 caractères');
        if (!/[A-Z]/.test(value))
          return t(
            'forms.passwordNeedsUppercase',
            'Le mot de passe doit contenir au moins une majuscule'
          );
        if (!/[a-z]/.test(value))
          return t(
            'forms.passwordNeedsLowercase',
            'Le mot de passe doit contenir au moins une minuscule'
          );
        if (!/[0-9]/.test(value))
          return t(
            'forms.passwordNeedsNumber',
            'Le mot de passe doit contenir au moins un chiffre'
          );
        return null;
      },
      confirmPassword: (value: string, values) =>
        value === values.password
          ? null
          : t('forms.passwordsDoNotMatch', 'Les mots de passe ne correspondent pas'),
      firstName: (value: string) =>
        value.length < 2
          ? t('forms.firstNameTooShort', 'Le prénom doit contenir au moins 2 caractères')
          : null,
      lastName: (value: string) =>
        value.length < 2
          ? t('forms.lastNameTooShort', 'Le nom doit contenir au moins 2 caractères')
          : null,
    },
    transformValues: values => {
      const { confirmPassword: _confirmPassword, ...rest } = values;

      let utmDataForApi = {};
      try {
        const utmDataString = sessionStorage.getItem('utm_data');
        if (utmDataString) {
          const utmDataFromStorage = JSON.parse(utmDataString);
          utmDataForApi = {
            utmSource: utmDataFromStorage.utm_source,
            utmMedium: utmDataFromStorage.utm_medium,
            utmCampaign: utmDataFromStorage.utm_campaign,
            utmTerm: utmDataFromStorage.utm_term,
            utmContent: utmDataFromStorage.utm_content,
          };
        }
      } catch (e) {
        console.error('Failed to parse and transform UTM data from sessionStorage', e);
      }

      return {
        ...rest,
        ...utmDataForApi,
        acceptedTermsPrivacyVersion:
          import.meta.env.VITE_TERMS_PRIVACY_VERSION ?? `unknown version ${dayjs().toISOString()}`,
      };
    },
  });
};
